/**
 * Helper: Format Nosto
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 *
 */
/* eslint-disable no-constant-binary-expression */
import { formatMoney } from '~/helpers/format-money'

/**
 * Format Nosto API product.
 * @param {Object} product - Nosto formatted product.
 * @returns {Object}
 */
export function formatNostoProduct(product) {
  const handle = product.url.split('/products/')[1]

  const formattedProduct = {
    available: product.skus.some((sku) => sku.available),
    compareAtPrice: formatNostoPrice(product.skus, 'list_price', product.price_currency_code),
    description: product.description,
    handle,
    id: Number(product.product_id),
    images: formatNostoImages(product),
    options: formatNostoOptions(product.skus),
    price: formatNostoPrice(product.skus, 'price', product.price_currency_code),
    tags: [
      ...product.tags1,
      ...product.tags2,
      ...product.tags3,
    ],
    title: product.name,
    type: product.custom_fields.productType,
    url: `//${location.host}${cnvs.routes.normalisedRoot}products/${handle}`,
    vendor: product.brand,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.giftCard = formattedProduct.type === 'Gift Cards'
  formattedProduct.variants = formatNostoVariants(product, formattedProduct.giftCard)

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  /**
   * Product badges.
   */
  formattedProduct.badges = formattedProduct.tags.map((tag) => {
    if (!tag.includes('Badge:')) {
      return false
    }

    return tag.split('Badge:')[1].trim()
  }).filter(Boolean)

  /**
   * Format metafields.
   * - Add each metafield to object, then add case.
   */
  const metafields = {
    colours: false,
    custom_id: '',
    vendor_link: '',
  }

  Object.keys(metafields).forEach((key) => {
    const object = product.metafields?.find((metafield) => {
      return metafield?.key === key
    })

    if (!object) {
      return
    }

    let value = object.value
    let formattedKey = key

    switch (key) {
      case 'colours':
        value = JSON.parse(object.value)
        break

      case 'custom_id':
        formattedKey = 'customId'
        delete metafields[key]
        break

      case 'vendor_link':
        value = value.replace(
          'https://childsplayredirect.com/',
          `//${location.host}${cnvs.routes.normalisedRoot}`,
        )

        formattedKey = 'vendorLink'
        delete metafields[key]
        break
    }

    metafields[formattedKey] = value
  })

  return {
    ...formattedProduct,
    ...metafields,
  }
}

/**
 * Format Nosto API product prices.
 * @param {Array} variants - Nosto format variants.
 * @param {String} property - Property to look at.
 * @param {String} currencyCode - Nosto currency code.
 * @return {Object}
 */
function formatNostoPrice(variants, property, currencyCode) {
  const prices = variants
    .map((variant) => variant[property])
    .sort()

  const max = isNaN(prices[prices.length - 1])
    ? false
    : Number(prices[prices.length - 1]) * 100

  const min = isNaN(prices[0])
    ? false
    : Number(prices[0]) * 100

  return {
    amount: min,
    currencyCode,
    max,
    min,
    varies: max !== min,
  }
}

/**
 * Format Nosto API images.
 * @param {Object} product - Nosto formatted product.
 * @returns {Array}
 */
function formatNostoImages(product) {
  const images = [
    product.image_url,
    ...product.alternate_image_urls,
  ]

  return images.map((src, id) => {
    return {
      alt: '',
      height: 0,
      id,
      src,
      variant: false,
      width: 0,
    }
  })
}

/**
 * Format Nosto API options.
 * @param {Array} variants - Nosto format variants.
 * @returns {Array}
 */
function formatNostoOptions(variants) {
  const options = {}

  variants.forEach((variant) => {
    Object.entries(variant.custom_fields).forEach(([name, value]) => {
      if (!options[name]) {
        options[name] = [value]
        return
      }

      options[name].push(value)
    })
  })

  return Object.entries(options).map(([name, values], id) => {
    const duplicates = []

    const formattedValues = values.filter((value) => {
      if (duplicates.includes(value)) {
        return false
      }

      duplicates.push(value)
      return true
    })

    return {
      id,
      name,
      values: formattedValues,
    }
  })
}

/**
 * Format Nosto API product variants.
 * @param {Object} product - Nosto formatted product.
 * @param {Boolean} isGiftCard - Product type is gift card.
 * @returns {Array}
 */
function formatNostoVariants(product, isGiftCard) {
  return product.skus.map((variant) => {
    const formattedVariant = {
      available: variant.available,
      compareAtPrice: {
        amount: isNaN(variant.list_price)
          ? false
          : Number(variant.list_price) * 100,
        currencyCode: product.price_currency_code,
      },
      id: Number(variant.id),
      image: false,
      inventory: 100,
      price: {
        amount: Number(variant.price) * 100 ?? false,
        currencyCode: product.price_currency_code,
      },
      selectedOptions: Object.entries(variant.custom_fields).map(([name, value]) => {
        return {
          name,
          value,
        }
      }),
    }

    /**
     * Wait for prices to be formatted before determining title.
     * - Gift cards show the price as their title for multi-currency purposes.
     */
    formattedVariant.title = isGiftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
      : variant.name

    return formattedVariant
  })
}
